import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import {
  School,
  GraduationCap,
  Users,
  BookOpen,
  Loader2,
  Clock,
  ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

interface ClassCourse {
  _id: string;
  title: string;
  thumbnail?: string;
  description?: string;
  level: string;
  duration?: number;
  category?: { _id: string; name: string } | null;
  professor?: { _id: string; name: string; avatar?: string } | null;
}

interface MyClassData {
  _id: string;
  name: string;
  level: string;
  description?: string;
  professors: Array<{ _id: string; name: string; email: string; avatar?: string }>;
  students: Array<{ _id: string; name: string; email: string; avatar?: string }>;
  courses: ClassCourse[];
}

const StudentMyClass: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<MyClassData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClass();
  }, []);

  const loadClass = async () => {
    try {
      setLoading(true);
      const response = await api.get('/student/class');
      setData(response.data.class || null);
    } catch (error) {
      console.error('Error loading class:', error);
      toast.error('Erreur lors du chargement de la classe');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin h-10 w-10 text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Ma Classe</h1>

        {!data ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-16">
            <div className="text-gray-400 mb-4">
              <School className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune classe assignée</h3>
            <p className="text-gray-500">
              Vous n'êtes pas encore affecté à une classe. Contactez votre administration.
            </p>
          </div>
        ) : (
          <>
            {/* Class Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <div className="w-14 h-14 bg-brand-600 text-white rounded-xl flex items-center justify-center mr-4">
                    <School className="h-7 w-7" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{data.name}</h2>
                    <div className="flex items-center mt-1 space-x-3">
                      <span className="inline-block px-2 py-0.5 text-xs font-medium bg-brand-50 text-brand-700 rounded-full">
                        {data.level}
                      </span>
                      {data.professors.length > 0 && (
                        <span className="text-sm text-gray-600">
                          Professeurs : <span className="font-medium">{data.professors.map((p) => p.name).join(', ')}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {data.description && (
                <p className="mt-4 text-sm text-gray-600">{data.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Classmates */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center">
                  <Users className="h-4 w-4 mr-2 text-brand-600" />
                  <h3 className="font-semibold text-gray-900">
                    Camarades de classe ({data.students.length})
                  </h3>
                </div>
                {data.students.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <GraduationCap className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    Aucun autre étudiant
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {data.students.map((student) => (
                      <li key={student._id} className="px-6 py-3 flex items-center">
                        <div className="w-9 h-9 rounded-full bg-brand-600 text-white flex items-center justify-center text-sm font-medium mr-3">
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{student.name}</p>
                          <p className="text-xs text-gray-500 truncate">{student.email}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Class courses */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-brand-600" />
                    Cours de la classe ({data.courses.length})
                  </h3>
                </div>

                {data.courses.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-10">
                    <BookOpen className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500">Aucun cours affecté à votre classe pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {data.courses.map((course) => (
                      <div
                        key={course._id}
                        onClick={() => navigate(`/student/courses/${course._id}`)}
                        className="flex items-center bg-white rounded-lg shadow-sm border border-gray-200 p-3 hover:shadow-md transition-shadow cursor-pointer"
                      >
                        {course.thumbnail ? (
                          <img
                            src={course.thumbnail}
                            alt={course.title}
                            className="w-14 h-14 rounded object-cover mr-3 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-brand-100 text-brand-600 rounded flex items-center justify-center mr-3 flex-shrink-0">
                            <BookOpen className="h-5 w-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{course.title}</p>
                          <div className="flex items-center text-xs text-gray-500 mt-1 space-x-3">
                            <span>{course.category?.name || 'Sans catégorie'}</span>
                            {course.duration ? (
                              <span className="flex items-center">
                                <Clock className="h-3 w-3 mr-0.5" />
                                {course.duration}h
                              </span>
                            ) : null}
                            {course.professor?.name && <span>{course.professor.name}</span>}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default StudentMyClass;